import axios from 'axios'
import { conflict, projectDeactivate, unauthorized } from './statusCodes'
import { getLocalStorage, isSuccess, setLocalStorage } from '../utils/helper'
import { logout } from '../hooks/CookiesHook'
import { notifyPermissionDenied } from '../utils/permissionDenied'
import { withDeploymentBase } from '../utils/deploymentPath'

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASEURL,
  timeout: 60000,
})

// Both 401 (unauthorized) and 409 (conflict) get reused by the backend for two
// different things it doesn't otherwise distinguish: the token is genuinely dead
// (expired/revoked), or the token is fine but this resource/action is off-limits
// for this user (access denied). Refreshing then retrying the original request
// once is the only way to tell them apart — a retry that still fails with a
// freshly refreshed token proves it was never a token problem, so we show
// Permission Denied instead of logging the user out. Only a failed refresh
// (session genuinely dead) logs out.
export const handleUnauthorized = async (originalConfig: any) => {
  let newAccessToken: string | undefined
  let refreshFailure: any
  try {
    const refreshRes = await axios.post(
      `${process.env.REACT_APP_API_BASEURL}v1/auth/refresh-token`,
      {},
      {
        headers: {
          'access-token': `${getLocalStorage('access_token')}`,
          'refresh-token': `${getLocalStorage('refresh_token')}`,
        },
      },
    )
    if (isSuccess(refreshRes) && refreshRes?.data?.meta?.access_token) {
      newAccessToken = refreshRes.data.meta.access_token
    } else {
      refreshFailure = refreshRes
    }
  } catch (err) {
    refreshFailure = err
  }

  if (!newAccessToken) {
    logout()
    return Promise.reject(refreshFailure)
  }
  setLocalStorage('access_token', newAccessToken)

  try {
    const retryRes = await axios({ ...originalConfig, headers: { ...originalConfig.headers, 'access-token': newAccessToken } })
    // Only the backend's own authorization codes mean access denied here — any other
    // failure (validation, availability, etc.) must be propagated as a normal error,
    // not shown as a permission denial.
    if (retryRes?.data?.meta?.code === unauthorized || retryRes?.data?.meta?.code === conflict) {
      notifyPermissionDenied()
    }
    return retryRes
  } catch (err: any) {
    if (err?.response?.status === unauthorized || err?.response?.status === conflict) {
      notifyPermissionDenied()
    }
    return Promise.reject(err)
  }
}

api.interceptors.response.use(
  (response: any) => {
    if (response?.data?.meta?.code === unauthorized || response?.data?.meta?.code === conflict) {
      return handleUnauthorized(response.config)
    }
    if (response?.data?.meta?.code === projectDeactivate) {
      window.location.href = withDeploymentBase(`/project-management?restricted=${response?.data?.meta.message}`)
    }

    return response
  },
  function (error) {
    if (error.response?.status === unauthorized || error.response?.status === conflict) {
      return handleUnauthorized(error.config)
    }
    return Promise.reject(error.response)
  },
)

api.interceptors.request.use(
  (config: any) => {
    config.headers = {
      'access-token': `${getLocalStorage('access_token')}`,
      // "ngrok-skip-browser-warning": "1"
    }

    if (config?.url?.includes('auth/workspaces') || config?.url?.includes('select-workspace') || config?.url?.includes('change-workspace')) {
      config.headers = {
        'temp-token': `${getLocalStorage('temp_token')}`,
        'access-token': `${getLocalStorage('access_token')}`,
        'refresh-token': `${getLocalStorage('refresh_token')}`,
        // "ngrok-skip-browser-warning": "1"
      }
    }

    if (config?.url?.includes('auth/logout')) {
      config.headers = {
        'access-token': `${getLocalStorage('access_token')}`,
        'refresh-token': `${getLocalStorage('refresh_token')}`,
        temp_token: `${getLocalStorage('temp_token')}`,
        // "ngrok-skip-browser-warning": "1"
      }
    }

    return config
  },
  function (error: any) {
    return Promise.reject(error)
  },
)

export default api
